import PropTypes from 'prop-types';
import React, { useState, useRef } from 'react';
import { IoMdAdd } from 'react-icons/io';
import { useFieldArray, useFormContext } from 'react-hook-form';
import VariantSVG from '@/assets/svg/icon_manageproducts.svg';
import { PrimaryOutlineButton } from '@/components/button/button-component';
import { Heading2Text } from '@/utils/global-fonts';
import { styledOption as Styled } from './options-variant-style';
import OptionsVariantModal from './options-variant-modal';
import OptionsVariantAll from './options-variant-all';

/**
 * Represents a hierarchical structure of files and folders.
 * @param {object} props - Component props
 * @param {string} props.name - The hook form name.
 * @param {Function} props.createVariantDefaults - Function to create default variant data.
 * @param {string} props.type - The type of the option.`
 * @param {object} props.originalOptions - The original options.
 * @returns {JSX.Element}
 */
const OptionsVariantComponent = ({
  name,
  createVariantDefaults,
  type,
  originalOptions,
}) => {
  const { control } = useFormContext();
  const addOptionsRef = useRef(null);

  const methods = useFieldArray({
    control,
    name,
  });

  const { fields: variants = [], append, remove, update } = methods;

  const [isOpen, setIsOpen] = useState(false);
  const [editData, setEditData] = useState(null);

  const handleOpenModal = () => {
    setIsOpen(true);
  };

  const renderVariants = () =>
    variants.length === 0 &&
    type !== 'view' && (
      <Styled.NoOptionAvailableWrapper>
        <Styled.NoOptionImg src={VariantSVG} alt="No variant img" />
        <Heading2Text>No Variant</Heading2Text>
        <Styled.StyledBodyText>
          Click the button below to add a variant to this option
        </Styled.StyledBodyText>
        <Styled.ButtonContainer ref={addOptionsRef}>
          <PrimaryOutlineButton
            title="Add Variant"
            icon={<IoMdAdd size={16} />}
            margin="10px 0px 0px 0px"
            onClick={handleOpenModal}
          />
        </Styled.ButtonContainer>
      </Styled.NoOptionAvailableWrapper>
    );

  const renderVariantTable = () =>
    variants.length > 0 && (
      <Styled.Container>
        <OptionsVariantAll
          setIsOpen={setIsOpen}
          setEditData={setEditData}
          remove={remove}
          type={type}
        />
      </Styled.Container>
    );

  return (
    <>
      {renderVariants()}
      {renderVariantTable()}
      <OptionsVariantModal
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        editData={editData}
        setEditData={setEditData}
        createVariantDefaults={createVariantDefaults}
        append={append}
        update={update}
        originalOptions={originalOptions}
        type={type}
      />
    </>
  );
};

OptionsVariantComponent.defaultProps = {
  originalOptions: {},
};

OptionsVariantComponent.propTypes = {
  name: PropTypes.string.isRequired,
  createVariantDefaults: PropTypes.func.isRequired,
  type: PropTypes.string.isRequired,
  originalOptions: PropTypes.object,
};

export default OptionsVariantComponent;
